import { useState } from "react";
import { Check, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { clearRestoreCache } from "@/lib/restore";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

/* Near-miss candidates for items the bulk search couldn't match exactly. `rows`: [{ key, title, subtitle, near[], item }],
   where `item` is the /artwork/approve payload without url ({album_id} or {name}). */
export const NearMissReview = ({ rows, noun, onSaved, testid }) => {
  const [busy, setBusy] = useState({});
  const [hidden, setHidden] = useState(() => new Set());
  const [confirm, setConfirm] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [progress, setProgress] = useState(null);
  const visible = rows.filter((r) => !hidden.has(r.key) && r.near?.length);
  if (!visible.length) return null;

  const approve = async (items) => {
    const { data } = await api.post("/artwork/approve", { items }, { timeout: 90000 });
    clearRestoreCache();
    const okIds = new Set(data.results.filter((x) => x.ok).map((x) => x.id));
    setHidden((h) => new Set([...h, ...rows.filter((r) => okIds.has(r.item.album_id || r.item.name)).map((r) => r.key)]));
    const failed = data.results.filter((x) => !x.ok);
    if (failed.length) {
      const nameOf = (id) => rows.find((r) => (r.item.album_id || r.item.name) === id)?.title || id;
      toast.error(`${failed.length} could not be saved — ${failed.slice(0, 3).map((x) => `${nameOf(x.id)}: ${x.error}`).join("; ")}${failed.length > 3 ? "…" : ""}`, { duration: 9000 });
    }
    if (data.saved) onSaved?.(data.saved);
    return data;
  };
  const one = async (row, cand) => {
    setBusy((b) => ({ ...b, [row.key]: cand.url }));
    try { const d = await approve([{ ...row.item, url: cand.url }]); if (d.saved) toast.success(`Saved ${noun} for ${row.title}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy((b) => { const n = { ...b }; delete n[row.key]; return n; }); }
  };
  const BATCH = 4;
  const all = async () => {
    setConfirm(false); setBulk(true);
    const items = visible.map((r) => ({ ...r.item, url: r.near[0].url }));
    let saved = 0;
    try {
      for (let i = 0; i < items.length; i += BATCH) {
        setProgress({ done: i, total: items.length });
        saved += (await approve(items.slice(i, i + BATCH))).saved;
      }
      toast.success(`Saved ${noun} for ${saved} of ${items.length}`);
    } catch (e) { toast.error(`${formatApiError(e)} — ${saved} saved so far; the rest can be retried.`); }
    finally { setBulk(false); setProgress(null); }
  };
  const skip = (row) => setHidden((h) => new Set([...h, row.key]));

  return (
    <section className="mb-10 rounded-lg border border-amber-200 bg-amber-50/40 p-6" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">Review near misses</p>
          <h2 className="mt-1 font-heading text-lg">{visible.length} {visible.length === 1 ? "item" : "items"} with close-but-not-exact matches</h2>
          <p className="mt-1 text-xs text-zinc-500">Tap a picture to approve it — it's saved immediately. Approve all uses the first pick of every row.</p>
        </div>
        <button type="button" onClick={() => setConfirm(true)} disabled={bulk} data-testid={`${testid}-approve-all`}
          className="flex h-10 items-center gap-2 rounded-full bg-amber-600 px-5 text-sm font-medium text-white transition-transform hover:scale-[1.03] disabled:opacity-50">
          {bulk ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{progress ? `Saving ${Math.min(progress.done + BATCH, progress.total)} of ${progress.total}…` : "Approve all top picks"}
        </button>
      </div>
      <ul className="mt-6 divide-y divide-amber-100" data-testid={`${testid}-list`}>
        {visible.map((row) => (
          <li key={row.key} className="flex flex-wrap items-center gap-4 py-4" data-testid={`${testid}-row-${row.key}`}>
            <div className="min-w-0 flex-1 basis-56">
              <p className="truncate text-sm font-medium text-zinc-900">{row.title}</p>
              {row.subtitle && <p className="truncate text-xs text-zinc-500">{row.subtitle}</p>}
            </div>
            <div className="flex items-center gap-3">
              {row.near.map((c, i) => {
                const saving = busy[row.key] === c.url;
                return (
                  <button key={c.url} type="button" onClick={() => one(row, c)} disabled={!!busy[row.key] || bulk} title={`${c.artist}${c.album ? ` — ${c.album}` : ""}${c.year ? ` (${c.year})` : ""} · ${c.source}`}
                    data-testid={`${testid}-pick-${row.key}-${i}`}
                    className={`group relative h-20 w-20 overflow-hidden rounded-md border bg-white transition-transform hover:scale-[1.04] disabled:opacity-60 ${i === 0 ? "border-amber-400 ring-2 ring-amber-200" : "border-zinc-200"}`}>
                    <img src={c.thumb || c.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                    <span className="absolute inset-x-0 bottom-0 truncate bg-zinc-900/80 px-1 py-0.5 text-[9px] text-white">{c.album || c.artist}</span>
                    <span className="absolute inset-0 flex items-center justify-center bg-amber-600/80 text-white opacity-0 transition-opacity group-hover:opacity-100">{saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={18} />}</span>
                  </button>
                );
              })}
              <button type="button" onClick={() => skip(row)} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-200 hover:text-zinc-900" title="Skip" data-testid={`${testid}-skip-${row.key}`}><X size={14} /></button>
            </div>
          </li>
        ))}
      </ul>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`${testid}-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve all top picks?</AlertDialogTitle>
            <AlertDialogDescription>The first candidate of each of the {visible.length} rows will be saved as the {noun}. You can still replace any of them later from the album or artist page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-confirm-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={all} data-testid={`${testid}-confirm-ok`} className="bg-amber-600 hover:bg-amber-700">Approve {visible.length}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
